import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

// Request deduplication cache
const pendingRequests = new Map();

// Data cache with timestamps
const dataCache = new Map();

// Expose cache for clearing (used by useCacheClear composable)
if (typeof window !== "undefined") {
  window.__VUEX_INVENTORY_CACHE__ = dataCache;
}

const state = {
  // Inventory
  inventory: [],
  loadingInventory: false,

  // Pagination
  inventoryPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  inventoryFilters: {
    query: "",
    sort: "name",
    include: "has_rra_ebm_metadata",
  },

  // Error handling
  error: null,
};

const getters = {
  // Inventory
  inventory: (state) => state.inventory,
  loadingInventory: (state) => state.loadingInventory,
  inventoryPagination: (state) => state.inventoryPagination,
  inventoryFilters: (state) => state.inventoryFilters,

  // Errors
  error: (state) => state.error,
};

const mutations = {
  // Inventory
  SET_INVENTORY(state, inventory) {
    state.inventory = inventory;
  },
  SET_LOADING_INVENTORY(state, loading) {
    state.loadingInventory = loading;
  },
  SET_INVENTORY_PAGINATION(state, pagination) {
    state.inventoryPagination = { ...state.inventoryPagination, ...pagination };
  },
  SET_INVENTORY_FILTERS(state, filters) {
    state.inventoryFilters = { ...state.inventoryFilters, ...filters };
  },

  // Error handling
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== INVENTORY ====================

  async fetchInventory(
    { commit, state },
    { storeId, filters = {}, page, perPage, forceRefresh = false } = {}
  ) {
    const pageNum = page || state.inventoryPagination.page;
    const perPageNum = perPage || state.inventoryPagination.per_page;
    const cacheKey = `inventory-${storeId}-${JSON.stringify(
      filters
    )}-${pageNum}-${perPageNum}`;

    // Check cache first (unless forceRefresh) - BEFORE setting loading state
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.inventory.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached inventory (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_INVENTORY", cached.data.items || []);
        commit("SET_INVENTORY_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating inventory request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_INVENTORY", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const params = {
          page: pageNum,
          per_page: perPageNum,
          ...state.inventoryFilters,
          ...filters,
        };

        const response = await http.get(`/v2/store/inventory/${storeId}`, {
          params,
        });

        const data = response.data || {};

        // Cache the result
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        commit("SET_INVENTORY", data.items || []);
        commit("SET_INVENTORY_PAGINATION", {
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          pages: data.pages,
        });

        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message || "Failed to load inventory";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_INVENTORY", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Update filters
  updateInventoryFilters({ commit }, filters) {
    commit("SET_INVENTORY_FILTERS", filters);
  },

  // Clear cache
  clearInventoryCache() {
    dataCache.clear();
    pendingRequests.clear();
    console.log("🗑️ Inventory cache cleared");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
